import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const FEATURES = [
  { icon: '🧾', title: 'Professional Invoices', desc: 'Create GST-ready invoices with live A4 preview and instant PDF export.' },
  { icon: '📧', title: 'Email & Share', desc: 'Send invoices directly to clients with branded PDF attachments.' },
  { icon: '👥', title: 'Customer Management', desc: 'Maintain a complete client directory with search and filters.' },
  { icon: '📝', title: 'Quotes & Estimates', desc: 'Convert approved quotes to invoices in a single click.' },
  { icon: '💳', title: 'Payment Tracking', desc: 'Track dues, record payments, and flag overdue invoices.' },
  { icon: '📊', title: 'Reports & Insights', desc: 'Understand your revenue trends with clean visual reports.' },
];

function useInView(ref) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold: 0.15 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref]);
  return inView;
}

function RevealSection({ children, delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref);
  return (
    <div ref={ref} style={{ opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(40px)', transition: `opacity 0.7s ${delay}s ease, transform 0.7s ${delay}s ease` }}>
      {children}
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [hoveredFeature, setHoveredFeature] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 50, y: 50 });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    const onMouse = (e) => setMousePos({ x: (e.clientX / window.innerWidth) * 100, y: (e.clientY / window.innerHeight) * 100 });
    window.addEventListener('scroll', onScroll);
    window.addEventListener('mousemove', onMouse);
    return () => { window.removeEventListener('scroll', onScroll); window.removeEventListener('mousemove', onMouse); };
  }, []);

  const S = {
    btn: { display: 'inline-flex', alignItems: 'center', gap: 8, padding: '13px 28px', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer', border: 'none', fontFamily: 'Inter,sans-serif', transition: 'all 0.25s cubic-bezier(0.4,0,0.2,1)' },
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        * { box-sizing:border-box; margin:0; padding:0; }
        body { font-family:'Inter',sans-serif; background:#fff; color:#1e293b; }
        html { scroll-behavior:smooth; }

        @keyframes heroIn  { from{opacity:0;transform:translateY(28px)} to{opacity:1;transform:translateY(0)} }
        @keyframes floatY  { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-14px)} }
        @keyframes gradPan { 0%{background-position:0% 50%} 50%{background-position:100% 50%} 100%{background-position:0% 50%} }
        @keyframes ripple  { to{transform:scale(4);opacity:0} }
        @keyframes tickerMove { from{transform:translateX(0)} to{transform:translateX(-50%)} }

        .hero-h1  { animation:heroIn 0.7s 0.1s both; }
        .hero-p   { animation:heroIn 0.7s 0.25s both; }
        .hero-cta { animation:heroIn 0.7s 0.4s both; }
        .mock-float { animation:floatY 5s ease-in-out infinite; }

        .grad-text {
          background: linear-gradient(120deg,#0066ff,#6c3bf5,#00b8d9);
          background-size:300% 300%;
          -webkit-background-clip:text; -webkit-text-fill-color:transparent;
          background-clip:text; animation:gradPan 5s ease infinite;
        }

        .nav-pill { padding:8px 16px; border-radius:8px; font-size:14px; font-weight:500; color:#334155; cursor:pointer; transition:all 0.2s; text-decoration:none; }
        .nav-pill:hover { background:#f1f5f9; color:#0066ff; }

        .feat-card {
          background:#fff; border:1.5px solid #e2e8f0; border-radius:18px;
          padding:28px; transition:all 0.3s cubic-bezier(0.4,0,0.2,1); cursor:default;
        }
        .feat-card:hover { border-color:#0066ff; transform:translateY(-6px); box-shadow:0 20px 40px -12px rgba(0,102,255,0.15); }

        .zoho-btn-primary { background:linear-gradient(135deg,#0066ff,#6c3bf5); color:#fff; box-shadow:0 6px 20px rgba(0,102,255,0.35); }
        .zoho-btn-primary:hover { transform:translateY(-2px); box-shadow:0 10px 28px rgba(0,102,255,0.45); }
        .zoho-btn-ghost { background:#fff; color:#0066ff; border:2px solid #0066ff !important; }
        .zoho-btn-ghost:hover { background:#eff6ff; transform:translateY(-2px); }

        .ticker-wrap { overflow:hidden; border-top:1px solid #f1f5f9; border-bottom:1px solid #f1f5f9; padding:16px 0; background:#fafbff; }
        .ticker-inner { display:flex; gap:60px; white-space:nowrap; animation:tickerMove 18s linear infinite; }
        .ticker-item { font-size:14px; font-weight:600; color:#64748b; display:flex; align-items:center; gap:8px; }

        .step-line { position:absolute; top:32px; left:calc(50% + 40px); width:calc(100% - 80px); height:2px; background:linear-gradient(90deg,#0066ff,#6c3bf5); border-radius:2px; }

        ::-webkit-scrollbar { width:6px; } ::-webkit-scrollbar-track { background:#f8fafc; } ::-webkit-scrollbar-thumb { background:#cbd5e1; border-radius:3px; }
      `}</style>

      {/* NAV */}
      <nav style={{ position:'fixed', top:0, left:0, right:0, zIndex:200, height:64, display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 48px', background: scrolled ? 'rgba(255,255,255,0.95)' : '#fff', backdropFilter:'blur(12px)', borderBottom: scrolled ? '1px solid #e2e8f0' : '1px solid transparent', transition:'all 0.3s ease', boxShadow: scrolled ? '0 4px 20px rgba(0,0,0,0.06)' : 'none' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ width:32, height:32, background:'linear-gradient(135deg,#0066ff,#6c3bf5)', borderRadius:9, display:'flex', alignItems:'center', justifyContent:'center', fontSize:17 }}>🛡️</div>
          <span style={{ fontWeight:800, fontSize:18, color:'#0f172a', letterSpacing:'-0.3px' }}>InvoicePro</span>
        </div>
        <div style={{ display:'flex', gap:4, alignItems:'center' }}>
          {['Features','How it Works','Pricing'].map(l => <a key={l} href={`#${l.replace(/ /g,'').toLowerCase()}`} className="nav-pill">{l}</a>)}
        </div>
        <div style={{ display:'flex', gap:10 }}>
          <button className="nav-pill zoho-btn-ghost" style={S.btn} onClick={() => navigate('/login')}>Log In</button>
          <button style={{...S.btn}} className="zoho-btn-primary" onClick={() => navigate('/register')}>Get Started Free →</button>
        </div>
      </nav>

      {/* HERO */}
      <section style={{ paddingTop:140, paddingBottom:80, paddingLeft:48, paddingRight:48, background:`radial-gradient(ellipse at ${mousePos.x}% ${mousePos.y}%, rgba(0,102,255,0.07) 0%, transparent 55%), linear-gradient(160deg,#f0f4ff 0%,#fff 60%)`, minHeight:'92vh', display:'flex', alignItems:'center', justifyContent:'space-between', gap:40, overflow:'hidden', transition:'background 0.3s ease' }}>
        <div style={{ flex:'0 0 520px' }}>
          <div style={{ display:'inline-flex', alignItems:'center', gap:8, background:'#eff6ff', border:'1px solid #bfdbfe', color:'#2563eb', padding:'7px 16px', borderRadius:100, fontSize:13, fontWeight:600, marginBottom:24 }}>
            <span style={{ width:7, height:7, borderRadius:'50%', background:'#3b82f6', display:'inline-block', animation:'ripple 2s ease infinite' }} />
            GST-Ready Invoicing for Indian Businesses
          </div>
          <h1 className="hero-h1" style={{ fontSize:54, fontWeight:900, lineHeight:1.08, letterSpacing:'-1.5px', color:'#0f172a', marginBottom:22 }}>
            Invoice like a pro.<br /><span className="grad-text">Get paid faster.</span>
          </h1>
          <p className="hero-p" style={{ fontSize:19, color:'#64748b', lineHeight:1.75, marginBottom:36, maxWidth:440 }}>
            Create beautiful GST invoices, manage clients, send quotes, and track every payment — all in one clean dashboard.
          </p>
          <div className="hero-cta" style={{ display:'flex', gap:14, flexWrap:'wrap' }}>
            <button style={S.btn} className="zoho-btn-primary" onClick={() => navigate('/register')}>🚀 Start for Free — No Card Needed</button>
            <button style={S.btn} className="zoho-btn-ghost" onClick={() => navigate('/login')}>View Dashboard →</button>
          </div>
          <div style={{ marginTop:22, display:'flex', gap:24, color:'#94a3b8', fontSize:13, flexWrap:'wrap' }}>
            {['✅ Free forever','✅ PDF export','✅ Email invoices'].map(t => <span key={t}>{t}</span>)}
          </div>
        </div>

        {/* Mock invoice card */}
        <div className="mock-float" style={{ flex:'0 0 420px', background:'#fff', borderRadius:20, boxShadow:'0 30px 80px -20px rgba(0,102,255,0.2)', border:'1px solid #e0e7ff', padding:28, position:'relative', overflow:'hidden' }}>
          <div style={{ position:'absolute', top:0, left:0, right:0, height:5, background:'linear-gradient(90deg,#0066ff,#6c3bf5,#00b8d9)' }} />
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
            <div>
              <div style={{ fontWeight:800, fontSize:15, color:'#0f172a' }}>🛡️ Codevirus Security</div>
              <div style={{ color:'#94a3b8', fontSize:12, marginTop:2 }}>Lucknow, Uttar Pradesh</div>
            </div>
            <div style={{ textAlign:'right' }}>
              <div style={{ fontWeight:900, fontSize:22, color:'#0f172a', letterSpacing:'-0.5px' }}>INVOICE</div>
              <div style={{ background:'#dcfce7', color:'#16a34a', padding:'2px 10px', borderRadius:20, fontSize:11, fontWeight:700, marginTop:4, display:'inline-block' }}>✅ PAID</div>
            </div>
          </div>
          <div style={{ background:'#f8faff', borderRadius:12, padding:'14px 16px', marginBottom:16 }}>
            {[['Web Design — 20hrs','₹40,000'],['Domain & Hosting','₹5,000'],['GST 18%','₹8,100']].map(([d,a]) => (
              <div key={d} style={{ display:'flex', justifyContent:'space-between', padding:'7px 0', borderBottom:'1px solid #e2e8f0', fontSize:13, color:'#475569' }}>
                <span>{d}</span><span style={{ fontWeight:600 }}>{a}</span>
              </div>
            ))}
            <div style={{ display:'flex', justifyContent:'space-between', padding:'12px 0 0', fontSize:16, fontWeight:800, color:'#0f172a' }}>
              <span>Total Due</span><span style={{ color:'#0066ff' }}>₹53,100</span>
            </div>
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <div style={{ flex:1, padding:'10px 0', background:'#eff6ff', borderRadius:9, color:'#0066ff', textAlign:'center', fontSize:13, fontWeight:700 }}>⬇️ Download PDF</div>
            <div style={{ flex:1, padding:'10px 0', background:'#f0fdf4', borderRadius:9, color:'#16a34a', textAlign:'center', fontSize:13, fontWeight:700 }}>📧 Send Email</div>
          </div>
        </div>
      </section>

      {/* TICKER */}
      <div className="ticker-wrap">
        <div className="ticker-inner">
          {[...Array(2)].map((_, ri) => ['Professional PDFs','GST Compliant','Client CRM','Quote Builder','Payment Tracking','Multi-user','Email Invoices','Reports & Analytics'].map((t,i) => (
            <div key={ri+'-'+i} className="ticker-item">
              <span style={{ color:'#0066ff' }}>✦</span> {t}
            </div>
          )))}
        </div>
      </div>

      {/* STATS */}
      <RevealSection>
        <div style={{ display:'flex', justifyContent:'center', gap:0, background:'#0f172a', padding:'60px 48px', flexWrap:'wrap' }}>
          {[['10,000+','Invoices Generated'],['500+','Active Businesses'],['₹2 Cr+','Revenue Tracked'],['99.9%','Uptime SLA']].map(([v,l],i) => (
            <div key={l} style={{ flex:'1 1 180px', textAlign:'center', padding:'0 40px', borderRight: i < 3 ? '1px solid rgba(255,255,255,0.08)' : 'none' }}>
              <div style={{ fontSize:40, fontWeight:900, color:'#0066ff', letterSpacing:'-1px' }}>{v}</div>
              <div style={{ color:'#64748b', fontSize:14, fontWeight:500, marginTop:6 }}>{l}</div>
            </div>
          ))}
        </div>
      </RevealSection>

      {/* FEATURES */}
      <section id="features" style={{ padding:'100px 48px', background:'#f8faff' }}>
        <RevealSection>
          <div style={{ textAlign:'center', marginBottom:60 }}>
            <div style={{ display:'inline-block', background:'#eff6ff', color:'#2563eb', padding:'6px 16px', borderRadius:100, fontSize:13, fontWeight:700, marginBottom:14 }}>⚡ Everything you need</div>
            <h2 style={{ fontSize:42, fontWeight:900, color:'#0f172a', letterSpacing:'-1px', marginBottom:14 }}>Built for <span className="grad-text">Indian businesses</span></h2>
            <p style={{ fontSize:17, color:'#64748b', maxWidth:520, margin:'0 auto' }}>From freelancers to agencies — InvoicePro adapts to how you work.</p>
          </div>
        </RevealSection>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', gap:20, maxWidth:1100, margin:'0 auto' }}>
          {FEATURES.map((f,i) => (
            <RevealSection key={i} delay={i * 0.08}>
              <div className="feat-card" onMouseEnter={() => setHoveredFeature(i)} onMouseLeave={() => setHoveredFeature(null)}>
                <div style={{ fontSize:36, marginBottom:14, transition:'transform 0.3s', transform: hoveredFeature === i ? 'scale(1.2) rotate(-5deg)' : 'scale(1)' }}>{f.icon}</div>
                <h3 style={{ fontSize:17, fontWeight:700, color:'#0f172a', marginBottom:10 }}>{f.title}</h3>
                <p style={{ fontSize:14, color:'#64748b', lineHeight:1.75 }}>{f.desc}</p>
              </div>
            </RevealSection>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="howitworks" style={{ padding:'100px 48px', background:'#fff' }}>
        <RevealSection>
          <div style={{ textAlign:'center', marginBottom:70 }}>
            <h2 style={{ fontSize:42, fontWeight:900, color:'#0f172a', letterSpacing:'-1px', marginBottom:14 }}>Up and running in <span className="grad-text">3 minutes</span></h2>
          </div>
        </RevealSection>
        <div style={{ display:'flex', justifyContent:'center', gap:0, maxWidth:900, margin:'0 auto', position:'relative', flexWrap:'wrap' }}>
          {[{ n:'01', icon:'📝', t:'Create Account', d:'Sign up free. Your company name is auto-filled from your email domain.' },{ n:'02', icon:'🧾', t:'Build Invoice', d:'Add clients and items. See your A4 invoice update live as you type.' },{ n:'03', icon:'🚀', t:'Send & Get Paid', d:'Download PDF or email it straight to your client in one click.' }].map((s,i) => (
            <RevealSection key={s.n} delay={i * 0.15}>
              <div style={{ flex:'1 1 240px', textAlign:'center', padding:'0 32px', position:'relative' }}>
                {i < 2 && <div style={{ position:'absolute', top:28, left:'calc(50% + 50px)', width:'calc(100% - 100px)', height:2, background:'linear-gradient(90deg,#0066ff44,#6c3bf544)', display:'flex', alignItems:'center', justifyContent:'center' }}><span style={{ fontSize:18, color:'#0066ff' }}>›</span></div>}
                <div style={{ width:56, height:56, borderRadius:'50%', background:'linear-gradient(135deg,#0066ff,#6c3bf5)', color:'white', display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, margin:'0 auto 20px', boxShadow:'0 8px 24px rgba(0,102,255,0.3)' }}>{s.icon}</div>
                <div style={{ fontSize:11, fontWeight:800, color:'#0066ff', letterSpacing:2, marginBottom:10 }}>STEP {s.n}</div>
                <h3 style={{ fontSize:18, fontWeight:700, color:'#0f172a', marginBottom:10 }}>{s.t}</h3>
                <p style={{ fontSize:14, color:'#64748b', lineHeight:1.75 }}>{s.d}</p>
              </div>
            </RevealSection>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="testimonials" style={{ padding:'100px 48px', background:'#f8faff' }}>
        <RevealSection>
          <h2 style={{ textAlign:'center', fontSize:42, fontWeight:900, color:'#0f172a', letterSpacing:'-1px', marginBottom:60 }}>Trusted by <span className="grad-text">businesses</span></h2>
        </RevealSection>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', gap:20, maxWidth:1000, margin:'0 auto' }}>
          {[{ a:'R', n:'Ravi Sharma', r:'Freelance Designer', t:'"The invoice preview is gorgeous. Clients actually compliment my invoices now!"', c:'#0066ff' },{ a:'P', n:'Priya Singh', r:'Agency Owner', t:'"Auto company name from email is brilliant. Saved us hours of onboarding time."', c:'#6c3bf5' },{ a:'A', n:'Amit Joshi', r:'Business Consultant', t:'"This replaced Zoho for us. Cleaner, faster, and the PDF quality is excellent."', c:'#00b8d9' }].map((t,i) => (
            <RevealSection key={i} delay={i * 0.1}>
              <div className="feat-card" style={{ background:'#fff' }}>
                <div style={{ fontSize:20, marginBottom:14, color:'#f59e0b' }}>★★★★★</div>
                <p style={{ fontSize:15, color:'#475569', lineHeight:1.8, marginBottom:20, fontStyle:'italic' }}>{t.t}</p>
                <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                  <div style={{ width:42, height:42, borderRadius:'50%', background:`linear-gradient(135deg,${t.c},${t.c}99)`, color:'white', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:16 }}>{t.a}</div>
                  <div><div style={{ fontWeight:700, color:'#0f172a', fontSize:14 }}>{t.n}</div><div style={{ color:'#94a3b8', fontSize:12 }}>{t.r}</div></div>
                </div>
              </div>
            </RevealSection>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section id="pricing" style={{ padding:'100px 48px', background:'linear-gradient(135deg,#0066ff,#6c3bf5)', textAlign:'center' }}>
        <RevealSection>
          <div style={{ maxWidth:620, margin:'0 auto' }}>
            <div style={{ fontSize:48, marginBottom:20 }}>🚀</div>
            <h2 style={{ fontSize:44, fontWeight:900, color:'#fff', letterSpacing:'-1px', marginBottom:16 }}>Start invoicing today.<br />It's completely free.</h2>
            <p style={{ color:'rgba(255,255,255,0.75)', fontSize:17, marginBottom:40, lineHeight:1.7 }}>No credit card required. No setup fees. Unlimited invoices on free plan.</p>
            <div style={{ display:'flex', gap:14, justifyContent:'center', flexWrap:'wrap' }}>
              <button style={{...S.btn, background:'#fff', color:'#0066ff', boxShadow:'0 8px 24px rgba(0,0,0,0.2)'}} onClick={() => navigate('/register')}>✅ Create Free Account</button>
              <button style={{...S.btn, background:'rgba(255,255,255,0.15)', color:'#fff', border:'2px solid rgba(255,255,255,0.4)'}} onClick={() => navigate('/login')}>Sign In →</button>
            </div>
          </div>
        </RevealSection>
      </section>

      {/* FOOTER */}
      <footer style={{ background:'#0f172a', padding:'48px', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:20 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ width:30, height:30, background:'linear-gradient(135deg,#0066ff,#6c3bf5)', borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', fontSize:15 }}>🛡️</div>
          <span style={{ color:'#fff', fontWeight:800, fontSize:16 }}>InvoicePro</span>
          <span style={{ color:'#334155', fontSize:13 }}>© {new Date().getFullYear()} Codevirus Security</span>
        </div>
        <div style={{ display:'flex', gap:24 }}>
          {['Privacy Policy','Terms','Contact'].map(l => <span key={l} style={{ color:'#475569', fontSize:13, cursor:'pointer', transition:'color 0.2s' }} onMouseEnter={e=>e.target.style.color='#60a5fa'} onMouseLeave={e=>e.target.style.color='#475569'}>{l}</span>)}
        </div>
      </footer>
    </>
  );
}
